import { memo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import ScheduleIcon from '@material-ui/icons/Schedule';
import { Schedule } from '@material-ui/icons';
import AuditProvider from 'components/modules/service/Audit/AuditProvider';
import ExpansionList from '../../../../common/ExpansionList';
import { columnMeta, createSearchFields } from './Scheduled/config/index';
import {
  useIsFspConfigUpdating,
  useIsProfileLoading,
} from '../../../implementation/Fsp/Detail/common';
import ExpandablePanel from '../../../../common/ExpandablePanel';
import GuestScheduledDetailsRender from './Scheduled/config/GuestScheduledDetailsRender';
import GuestCancelPaymentDialog from './Scheduled/config/GuestCancelPaymentDialog';
import GuestScheduledActionsRenderer from './Scheduled/config/GuestScheduledActionsRenderer';

function guestScheduledDetailsRender(transaction) {
  return <GuestScheduledDetailsRender transaction={transaction} />;
}

const GuestScheduledTransactionsPanel = ({
  items,
  heightOffset,
  scrollTopOffset,
  fspId,
  loanNumber,
  onOpen,
  ...rest
}) => {
  const noop = () => undefined;
  const loading = useIsProfileLoading();
  const updating = useIsFspConfigUpdating();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const handleCancelClick = useCallback((payment) => {
    setSelectedPayment(payment);
    setCancelDialogOpen(true);
  }, []);

  const handleCancelDialogClose = useCallback(() => {
    setCancelDialogOpen(false);
    setSelectedPayment(null);
  }, []);

  const handleCancelSuccess = useCallback(() => {
    if (typeof rest.onCancelSuccess === 'function') {
      rest.onCancelSuccess(selectedPayment);
    }
    setCancelDialogOpen(false);
    setSelectedPayment(null);
  }, [rest, selectedPayment]);

  const ActionsRenderer = useCallback(
    (props) => {
      return (
        <GuestScheduledActionsRenderer
          {...props}
          onCancelClick={handleCancelClick}
        />
      );
    },
    [handleCancelClick]
  );

  return (
    <AuditProvider>
      <>
        <ExpandablePanel
          fullHeight={false}
          icon={Schedule}
          id="guest-scheduled-transactions"
          showProgress={loading || updating}
          title="Scheduled Transactions"
          onChange={(expanded) => {
            if (expanded && onOpen) onOpen();
          }}
        >
          <ExpansionList
            key={`scheduled-${items && items.length}`}
            ActionsRenderer={ActionsRenderer}
            columnMeta={columnMeta}
            countBack={false}
            createSearchFields={createSearchFields}
            detailsRender={guestScheduledDetailsRender}
            emptyTitle="No scheduled transactions found."
            ExpandablePanelProps={{
              heightOffset: heightOffset || 68,
              icon: ScheduleIcon,
              id: 'scheduled',
              scrollTopOffset: scrollTopOffset || 0,
              title: 'Scheduled Transactions',
              showProgress: false,
              defaultExpanded: false,
            }}
            data={items}
            loadAction={noop}
            clearAction={noop}
            {...rest}
          />
        </ExpandablePanel>
        <GuestCancelPaymentDialog
          open={cancelDialogOpen}
          payment={selectedPayment}
          fspId={fspId}
          loanNumber={loanNumber}
          onClose={handleCancelDialogClose}
          onCancelSuccess={handleCancelSuccess}
        />
      </>
    </AuditProvider>
  );
};

GuestScheduledTransactionsPanel.propTypes = {
  items: PropTypes.array,
  heightOffset: PropTypes.number,
  scrollTopOffset: PropTypes.number,
  fspId: PropTypes.string,
  loanNumber: PropTypes.string,
  onOpen: PropTypes.func,
};

GuestScheduledTransactionsPanel.defaultProps = {
  items: [],
  heightOffset: 68,
  scrollTopOffset: 0,
  fspId: null,
  loanNumber: null,
  onOpen: undefined,
};

export default memo(GuestScheduledTransactionsPanel);
